import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  LayoutDashboard, UploadCloud, Users, BookOpen, Settings2, PlayCircle,
  ScrollText, Receipt, Wallet, FileBarChart, ShieldCheck, History,
  Download, Trash2, Search, Plus, Pencil, FileSpreadsheet, Camera,
  UserPlus, IndianRupee, Clock, CalendarDays, ChevronRight, RefreshCw,
  Lock, Unlock, FileText, TrendingUp, CheckCircle2, XCircle, AlertCircle,
  Archive, HandCoins, Banknote, PlusCircle, MinusCircle, ArrowUpRight, ArrowDownRight
} from "lucide-react";

/**
 * Payroll Panel — Phase 1
 * -----------------------
 * Sub-modules:
 *   • Dashboard
 *   • Attendance Import  ← Phase 1 primary
 *   • Employee Master    ← Phase 1 primary
 *   • Employee Ledger    (placeholder — Phase 3)
 *   • Salary Rules       (placeholder — Phase 2, read-only default view here)
 *   • Payroll Processing (placeholder — Phase 2)
 *   • Salary Preview     (placeholder — Phase 2)
 *   • Salary Slip        (placeholder — Phase 2)
 *   • Payment Register   (placeholder — Phase 3)
 *   • Reports            (placeholder — Phase 4)
 *   • Settings           (Phase 1 read-only rules)
 *   • Audit Log          ← Phase 1 primary
 */

const NAV = [
  { id: "dashboard",       label: "Dashboard",         icon: LayoutDashboard },
  { id: "attendance",      label: "Attendance Import", icon: UploadCloud },
  { id: "employees",       label: "Employee Master",   icon: Users },
  { id: "ledger",          label: "Employee Ledger",   icon: BookOpen },
  { id: "calculation",     label: "Attendance Calc",   icon: BookOpen },
  { id: "rules",           label: "Salary Rules",      icon: Settings2 },
  { id: "advances",        label: "Advances",          icon: HandCoins },
  { id: "loans",           label: "Loans",             icon: Banknote },
  { id: "adjustments",     label: "Adjustments",       icon: PlusCircle },
  { id: "processing",      label: "Payroll Processing",icon: PlayCircle },
  { id: "preview",         label: "Salary Preview",    icon: ScrollText },
  { id: "slip",            label: "Salary Slip",       icon: Receipt },
  { id: "payments",        label: "Payment Register",  icon: Wallet },
  { id: "reports",         label: "Reports",           icon: FileBarChart },
  { id: "settings",        label: "Settings",          icon: ShieldCheck },
  { id: "audit",           label: "Audit Log",         icon: History },
];

const PayrollPanel = ({ user }) => {
  const hasAccess = user?.role === "admin" || user?.document_sub_panels?.payroll !== false;
  const [tab, setTab] = useState("dashboard");

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="payroll-access-denied">
        <Card className="max-w-md w-full border-red-200 bg-red-50/50">
          <CardContent className="pt-8 pb-8 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-red-500" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
            <p className="text-sm text-red-600">Contact your administrator to request Payroll access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-80px)] bg-slate-50" data-testid="payroll-panel">
      {/* Left navigation */}
      <aside className="w-60 shrink-0 border-r border-slate-200 bg-white overflow-y-auto" data-testid="payroll-nav">
        <div className="px-4 py-4 border-b border-slate-100 bg-gradient-to-br from-indigo-600 to-violet-700">
          <div className="text-white font-semibold text-base flex items-center gap-2">
            <IndianRupee className="w-4 h-4" /> Payroll
          </div>
          <div className="text-white/70 text-xs mt-0.5">Employee Salary & Attendance</div>
        </div>
        <nav className="py-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                data-testid={`payroll-nav-${item.id}`}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition
                  ${active ? "bg-indigo-50 text-indigo-700 border-r-2 border-indigo-600 font-medium"
                           : "text-slate-600 hover:bg-slate-50"}`}
              >
                <Icon className={`w-4 h-4 ${active ? "text-indigo-600" : "text-slate-400"}`} />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                    {item.badge}
                  </span>
                )}
                {item.phase && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                    {item.phase}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main workspace */}
      <main className="flex-1 overflow-y-auto p-6">
        {tab === "dashboard"  && <DashboardTab user={user} />}
        {tab === "attendance" && <AttendanceTab user={user} />}
        {tab === "employees"  && <EmployeesTab user={user} />}
        {tab === "calculation" && <CalculationTab user={user} />}
        {tab === "rules"      && <RulesTab user={user} />}
        {tab === "advances"   && <AdvancesTab user={user} />}
        {tab === "loans"      && <LoansTab user={user} />}
        {tab === "adjustments"&& <AdjustmentsTab user={user} />}
        {tab === "processing" && <ProcessingTab user={user} />}
        {tab === "preview"    && <PreviewTab user={user} />}
        {tab === "slip"       && <SlipTab user={user} />}
        {tab === "payments"   && <PaymentsTab user={user} />}
        {tab === "ledger"     && <LedgerTab user={user} />}
        {tab === "settings"   && <SettingsTab user={user} />}
        {tab === "audit"      && <AuditTab user={user} />}
        {tab === "reports"    && <ReportsTab user={user} />}
      </main>
    </div>
  );
};

/* ---------------------- Dashboard tab ---------------------- */

const StatCard = ({ label, value, icon: Icon, tint = "indigo", sub }) => (
  <Card className="border-slate-200 hover:shadow-md transition" data-testid={`payroll-card-${label.replace(/\s+/g,'-').toLowerCase()}`}>
    <CardContent className="pt-5 pb-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className={`text-2xl font-bold text-${tint}-700 mt-1`}>{value}</div>
          {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${tint}-50`}>
          <Icon className={`w-5 h-5 text-${tint}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [d, t] = await Promise.all([
        axios.get(`${API}/payroll/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API}/payroll/reports/dashboard-trend`, { headers: getAuthHeader() }),
      ]);
      setData(d.data);
      setTrend(t.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salaryData = trend?.salary_trend || [];
  const attData = trend?.attendance_trend || [];
  const otData = trend?.ot_trend || [];
  const deptData = trend?.dept_cost || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Live snapshot of headcount, attendance and payroll status</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="payroll-dashboard-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard label="Total Employees" value={data?.employees_total ?? "—"} icon={Users} tint="indigo" sub={`${data?.employees_active ?? 0} active`} />
        <StatCard label="Latest Batch"    value={data?.latest_batch?.total_employees ?? "—"} icon={CalendarDays} tint="violet" sub={data?.latest_batch?.filename || "No import yet"} />
        <StatCard label="Present (last day)" value={data?.latest_batch?.present_today ?? "—"} icon={ShieldCheck} tint="emerald" sub={data?.latest_batch?.period_to ? `on ${data.latest_batch.period_to}` : ""} />
        <StatCard label="Absent (last day)"  value={data?.latest_batch?.absent_today ?? "—"} icon={Clock} tint="rose" sub={data?.latest_batch?.period_to ? `on ${data.latest_batch.period_to}` : ""} />
        <StatCard label="OT (last day, h)"   value={data?.latest_batch?.ot_today_hours ?? 0} icon={Clock} tint="amber" />
        <StatCard label="Current Month Salary" value={formatMoney(data?.current_month_salary)} icon={IndianRupee} tint="indigo" sub="Estimated payable" />
        <StatCard label="Advances Outstanding" value={formatMoney(data?.advances_outstanding)} icon={Wallet} tint="orange" />
        <StatCard label="Loans Outstanding"    value={formatMoney(data?.loans_outstanding)}    icon={Wallet} tint="orange" />
      </div>

      {trend && (salaryData.length > 0 || deptData.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payroll-dashboard-charts">
          <ChartCard title="Salary Trend" subtitle="Total net salary paid per month">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={salaryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" stroke="#6b7280" style={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" style={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Line type="monotone" dataKey="total_net" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Attendance Trend" subtitle="Average attendance % per month">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={attData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" stroke="#6b7280" style={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" style={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="attendance_pct" stroke="#059669" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Overtime Trend" subtitle="Total OT hours per month">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={otData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" stroke="#6b7280" style={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" style={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}h`} />
                <Bar dataKey="ot_hours" fill="#f59e0b" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Department Cost" subtitle="Total net paid by department (all-time)">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={deptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" style={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="department" stroke="#6b7280" style={{ fontSize: 11 }} width={90} />
                <Tooltip formatter={(v) => formatMoney(v)} />
                <Bar dataKey="total_net" fill="#7c3aed" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {data?.latest_batch && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latest Attendance Import</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Info label="Filename" value={data.latest_batch.filename} />
              <Info label="Period From" value={data.latest_batch.period_from} />
              <Info label="Period To"   value={data.latest_batch.period_to} />
              <Info label="Employees"   value={data.latest_batch.total_employees} />
            </div>
          </CardContent>
        </Card>
      )}

      {((data?.latest_batch?.top_ot_employees?.length || 0) > 0 ||
        (data?.latest_batch?.top_late_employees?.length || 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(data?.latest_batch?.top_ot_employees?.length || 0) > 0 && (
            <Card data-testid="payroll-top-ot-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Top 5 Overtime — {data.latest_batch.period_from || ""}
                  {data.latest_batch.period_to ? ` → ${data.latest_batch.period_to}` : ""}
                </CardTitle>
                <div className="text-[11px] text-slate-500">
                  Employees with the most OT hours in the latest batch.
                  OT hours are tracked for visibility; payment depends on the
                  &ldquo;Pay for Overtime Hours&rdquo; rule in Payroll Settings.
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {data.latest_batch.top_ot_employees.map((e, idx) => {
                    const max = data.latest_batch.top_ot_employees[0]?.ot_hours || 1;
                    const pct = Math.max(4, Math.min(100, (e.ot_hours / max) * 100));
                    return (
                      <div key={e.employee_id || idx}
                           className="flex items-center gap-3 text-xs"
                           data-testid={`top-ot-row-${idx}`}>
                        <div className="w-5 text-slate-500 font-semibold text-right">#{idx + 1}</div>
                        <div className="w-40 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{e.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {e.employee_id}{e.department ? ` · ${e.department}` : ""}
                          </div>
                        </div>
                        <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                          <div className="h-2 bg-gradient-to-r from-amber-300 to-amber-500 rounded"
                               style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-14 text-right font-semibold text-amber-700">
                          {e.ot_hours}h
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {(data?.latest_batch?.top_late_employees?.length || 0) > 0 && (
            <Card data-testid="payroll-top-late-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-rose-500" />
                  Top 5 Late Logins — {data.latest_batch.period_from || ""}
                  {data.latest_batch.period_to ? ` → ${data.latest_batch.period_to}` : ""}
                </CardTitle>
                <div className="text-[11px] text-slate-500">
                  Employees with the most late-in days in the latest batch.
                  Deduction depends on the &ldquo;Late Deduction Rule&rdquo; in Payroll Settings.
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {data.latest_batch.top_late_employees.map((e, idx) => {
                    const max = data.latest_batch.top_late_employees[0]?.late_days || 1;
                    const pct = Math.max(4, Math.min(100, (e.late_days / max) * 100));
                    return (
                      <div key={e.employee_id || idx}
                           className="flex items-center gap-3 text-xs"
                           data-testid={`top-late-row-${idx}`}>
                        <div className="w-5 text-slate-500 font-semibold text-right">#{idx + 1}</div>
                        <div className="w-40 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{e.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {e.employee_id}{e.department ? ` · ${e.department}` : ""}
                          </div>
                        </div>
                        <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                          <div className="h-2 bg-gradient-to-r from-rose-300 to-rose-500 rounded"
                               style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-16 text-right font-semibold text-rose-700 whitespace-nowrap">
                          {e.late_days}d
                          {e.late_hours > 0 && (
                            <span className="text-[10px] text-slate-500 ml-1">
                              ({e.late_hours}h)
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {(data?.latest_batch?.perfect_attendance?.length || 0) > 0 && (
        <Card data-testid="payroll-perfect-attendance-card"
              className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Perfect Attendance — {data.latest_batch.perfect_attendance_count}
              {data.latest_batch.perfect_attendance_count === 1 ? " employee" : " employees"}
            </CardTitle>
            <div className="text-[11px] text-slate-600">
              Zero absences, zero late-ins, zero half-days in
              {data.latest_batch.period_from ? ` ${data.latest_batch.period_from}` : " the latest batch"}
              {data.latest_batch.period_to ? ` → ${data.latest_batch.period_to}` : ""}.
              Consider a shout-out or a small recognition.
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.latest_batch.perfect_attendance.map((e, idx) => (
                <div key={e.employee_id || idx}
                     className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-xs shadow-sm"
                     data-testid={`perfect-att-chip-${idx}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-slate-800">{e.name}</span>
                  {e.department && (
                    <span className="text-[10px] text-slate-500">· {e.department}</span>
                  )}
                  <span className="text-[10px] text-emerald-700 font-semibold">
                    {e.present_days}d
                  </span>
                </div>
              ))}
              {data.latest_batch.perfect_attendance_count > data.latest_batch.perfect_attendance.length && (
                <div className="flex items-center px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-xs text-emerald-700">
                  + {data.latest_batch.perfect_attendance_count - data.latest_batch.perfect_attendance.length} more
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm">{title}</CardTitle>
      {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
    <div className="text-sm text-slate-800 font-medium mt-0.5">{value || "—"}</div>
  </div>
);

const formatMoney = (v) => {
  const n = Number(v || 0);
  if (!n) return "₹0";
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

/* ---------------------- Attendance Import tab ---------------------- */

const AttendanceTab = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/payroll/attendance`, { headers: getAuthHeader() });
      setBatches(res.data || []);
    } catch { toast.error("Failed to load imports"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/payroll/attendance/import`, fd, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${res.data.total_employees} employees, ${res.data.created_employees.length} new`);
      setImportResult(res.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadFile = async (path, filename) => {
    try {
      const res = await axios.get(`${API}${path}`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm("Delete this attendance batch? Auto-created employees will be kept.")) return;
    try {
      await axios.delete(`${API}/payroll/attendance/${batchId}`, { headers: getAuthHeader() });
      toast.success("Batch deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-attendance-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Attendance Import</h1>
        <p className="text-sm text-slate-500 mt-1">Upload the monthly fingerprint machine export. The parser auto-detects columns & date rows.</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="border-2 border-dashed border-indigo-200 bg-indigo-50/40 rounded-lg p-6 flex flex-col items-center text-center">
            <UploadCloud className="w-10 h-10 text-indigo-500 mb-2" />
            <div className="text-sm text-slate-700 font-medium">Upload Attendance Sheet (.xls / .xlsx)</div>
            <div className="text-xs text-slate-500 mt-1">Auto-creates missing employees. Existing employees are matched by Emp ID.</div>
            <div className="mt-4 flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xls,.xlsx,.xlsm"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="payroll-file-input"
              />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="payroll-upload-btn">
                {uploading ? "Uploading…" : "Choose File"}
              </Button>
            </div>
          </div>

          {importResult && (
            <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded-lg p-4 text-sm" data-testid="payroll-import-result">
              <div className="font-semibold text-emerald-800">Import successful</div>
              <div className="text-emerald-700 mt-1">
                {importResult.total_employees} employees · <b>{importResult.created_employees.length} new</b> created · {importResult.existing_employees.length} matched existing
                <div className="text-xs mt-0.5 text-slate-600">Period: {importResult.period_from} → {importResult.period_to}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Import History</CardTitle>
            <Badge variant="outline">{batches.length}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
          ) : batches.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No imports yet. Upload your first attendance file above.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Filename</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((b) => (
                    <TableRow key={b.id} data-testid={`payroll-batch-row-${b.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                          {b.filename}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {b.period_from || "—"} <ChevronRight className="inline w-3 h-3" /> {b.period_to || "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium">{b.total_employees}</TableCell>
                      <TableCell className="text-xs">{b.uploaded_by_name || b.uploaded_by_email}</TableCell>
                      <TableCell className="text-xs">{new Date(b.uploaded_at).toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="outline" title="Download Salary Sheet"
                                  onClick={() => downloadFile(`/payroll/attendance/${b.id}/salary-sheet.xlsx`, `salary_${b.period_from || 'sheet'}.xlsx`)}
                                  data-testid={`payroll-salary-${b.id}`}>
                            <IndianRupee className="w-3.5 h-3.5" /> Salary
                          </Button>
                          <Button size="sm" variant="outline" title="Download Attendance Matrix"
                                  onClick={() => downloadFile(`/payroll/attendance/${b.id}/matrix.xlsx`, `matrix_${b.period_from || 'sheet'}.xlsx`)}
                                  data-testid={`payroll-matrix-${b.id}`}>
                            <Download className="w-3.5 h-3.5" /> Matrix
                          </Button>
                          <Button size="sm" variant="ghost" title="Delete"
                                  className="text-rose-600 hover:bg-rose-50"
                                  onClick={() => handleDelete(b.id)}
                                  data-testid={`payroll-delete-${b.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------------- Employees tab ---------------------- */

const EMPTY_EMP = {
  employee_id: "", name: "", department: "", designation: "", joining_date: "",
  monthly_salary: 0, hourly_salary: 0,
  bank_name: "", bank_account: "", ifsc: "", upi: "",
  pf_number: "", esic_number: "", pan: "", aadhaar: "", mobile: "",
  status: "active", remarks: "",
};

const EmployeesTab = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [status, setStatus] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // employee_id or null
  const [form, setForm] = useState(EMPTY_EMP);
  const photoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (dept) params.set("department", dept);
      if (status) params.set("status", status);
      const res = await axios.get(`${API}/payroll/employees?${params.toString()}`, { headers: getAuthHeader() });
      setRows(res.data || []);
    } catch { toast.error("Failed to load employees"); }
    finally { setLoading(false); }
  }, [q, dept, status]);
  useEffect(() => { load(); }, [load]);

  const departments = useMemo(() => Array.from(new Set(rows.map(r => r.department).filter(Boolean))), [rows]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_EMP); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e.employee_id); setForm({ ...EMPTY_EMP, ...e }); setDialogOpen(true); };

  const save = async () => {
    if (!form.employee_id?.trim()) { toast.error("Employee ID is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/payroll/employees/${editing}`, form, { headers: getAuthHeader() });
        toast.success("Employee updated");
      } else {
        await axios.post(`${API}/payroll/employees`, form, { headers: getAuthHeader() });
        toast.success("Employee created");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (emp) => {
    if (!window.confirm(`Delete ${emp.name || emp.employee_id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/payroll/employees/${emp.employee_id}`, { headers: getAuthHeader() });
      toast.success("Employee removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const uploadPhoto = async (emp, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/payroll/employees/${emp.employee_id}/photo`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo upload failed"); }
  };

  return (
    <div className="space-y-4" data-testid="payroll-employees-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employee Master</h1>
          <p className="text-sm text-slate-500 mt-1">All employees — auto-created from attendance imports + manual entries.</p>
        </div>
        <Button onClick={openCreate} data-testid="payroll-employee-add">
          <UserPlus className="w-4 h-4 mr-1" /> Add Employee
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, ID, mobile, dept…" className="pl-9" data-testid="payroll-employee-search" />
            </div>
            <Select value={dept || "__all"} onValueChange={(v) => setDept(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="payroll-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status || "__all"} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-32" data-testid="payroll-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Emp ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead className="text-right">Monthly Salary</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-6">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-6">No employees found.</TableCell></TableRow>
                ) : rows.map(e => (
                  <TableRow key={e.employee_id} data-testid={`payroll-employee-${e.employee_id}`}>
                    <TableCell className="font-mono text-xs">{e.employee_id}</TableCell>
                    <TableCell className="font-medium">{e.name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell>{e.department || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell>{e.designation || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-right">{formatMoney(e.monthly_salary)}</TableCell>
                    <TableCell className="text-xs">{e.mobile || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell>
                      <Badge variant={e.status === "active" ? "default" : "secondary"} className="text-xs">{e.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`payroll-emp-edit-${e.employee_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {user?.role === "admin" && (
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => del(e)} data-testid={`payroll-emp-del-${e.employee_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="payroll-emp-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Employee ID *" value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} disabled={!!editing} testid="emp-id" />
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="emp-name" />
            <Field label="Department" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="emp-dept" />
            <Field label="Designation" value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} testid="emp-desig" />
            <Field label="Joining Date" type="date" value={form.joining_date} onChange={(v) => setForm({ ...form, joining_date: v })} testid="emp-join" />
            <Field label="Mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testid="emp-mobile" />
            <Field label="Monthly Salary" type="number" value={form.monthly_salary} onChange={(v) => setForm({ ...form, monthly_salary: Number(v) || 0 })} testid="emp-monthly" />
            <Field label="Hourly Salary (optional)" type="number" value={form.hourly_salary} onChange={(v) => setForm({ ...form, hourly_salary: Number(v) || 0 })} testid="emp-hourly" />

            <Field label="Bank Name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} testid="emp-bank" />
            <Field label="Bank Account" value={form.bank_account} onChange={(v) => setForm({ ...form, bank_account: v })} testid="emp-acct" />
            <Field label="IFSC" value={form.ifsc} onChange={(v) => setForm({ ...form, ifsc: v })} testid="emp-ifsc" />
            <Field label="UPI ID" value={form.upi} onChange={(v) => setForm({ ...form, upi: v })} testid="emp-upi" />

            <Field label="PF Number" value={form.pf_number} onChange={(v) => setForm({ ...form, pf_number: v })} testid="emp-pf" />
            <Field label="ESIC Number" value={form.esic_number} onChange={(v) => setForm({ ...form, esic_number: v })} testid="emp-esic" />
            <Field label="PAN" value={form.pan} onChange={(v) => setForm({ ...form, pan: v })} testid="emp-pan" />
            <Field label="Aadhaar" value={form.aadhaar} onChange={(v) => setForm({ ...form, aadhaar: v })} testid="emp-aadhaar" />

            <div className="md:col-span-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="emp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1" data-testid="emp-remarks" />
            </div>

            {editing && (
              <div className="md:col-span-2 border-t pt-3">
                <Label className="mb-2 block">Photo (optional, ≤ 5MB)</Label>
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" ref={photoRef} className="hidden"
                         onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto({ employee_id: editing }, f); }} />
                  <Button variant="outline" onClick={() => photoRef.current?.click()} data-testid="emp-photo-btn">
                    <Camera className="w-4 h-4 mr-1" /> Upload Photo
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="payroll-emp-save">{editing ? "Save Changes" : "Create Employee"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", disabled, testid }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} type={type} disabled={disabled} className="mt-1" data-testid={testid} />
  </div>
);

/* ---------------------- Audit tab ---------------------- */

const AuditTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/payroll/audit-log?limit=500`, { headers: getAuthHeader() });
      setRows(res.data || []);
    } catch { toast.error("Failed to load audit log"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const badgeFor = (a) => {
    if (a.startsWith("attendance")) return "bg-indigo-100 text-indigo-700";
    if (a.startsWith("employee")) return "bg-emerald-100 text-emerald-700";
    if (a.startsWith("settings")) return "bg-amber-100 text-amber-700";
    return "bg-slate-100 text-slate-700";
  };

  return (
    <div className="space-y-4" data-testid="payroll-audit-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1">Every payroll action is recorded here.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
      </div>
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>When</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No activity yet.</TableCell></TableRow>
                ) : rows.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{e.user_name || e.user_email || "—"}</TableCell>
                    <TableCell><span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badgeFor(e.action)}`}>{e.action}</span></TableCell>
                    <TableCell className="text-xs font-mono">{e.target_type}:{e.target_id}</TableCell>
                    <TableCell className="text-xs">{e.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------------- Settings (Phase 1 read-only + basic edit) ---------------------- */

/* ---------------------- Advances / Loans / Adjustments shared employee picker ---------------------- */

const useEmployees = () => {
  const [emps, setEmps] = useState([]);
  useEffect(() => {
    axios.get(`${API}/payroll/employees`, { headers: getAuthHeader() })
      .then(r => setEmps(r.data || []))
      .catch(() => setEmps([]));
  }, []);
  return emps;
};

const EmpSelect = ({ value, onChange, testid }) => {
  const emps = useEmployees();
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid || "emp-select"}><SelectValue placeholder="Choose employee…" /></SelectTrigger>
      <SelectContent>
        {emps.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} — {e.name || "—"}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

/* ---------------------- Advances tab ---------------------- */

const AdvancesTab = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", amount: 0, reason: "", recovery_start_month: "", recovery_amount_per_month: 0, notes: "" });
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try { setLoading(true); const r = await axios.get(`${API}/payroll/advances`, { headers: getAuthHeader() }); setRows(r.data || []); }
    catch { toast.error("Load failed"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    if (!(form.amount > 0)) { toast.error("Amount must be > 0"); return; }
    if (!(form.recovery_amount_per_month > 0)) { toast.error("Monthly recovery must be > 0"); return; }
    try {
      await axios.post(`${API}/payroll/advances`, form, { headers: getAuthHeader() });
      toast.success("Advance recorded");
      setDialogOpen(false); load();
      setForm({ employee_id: "", amount: 0, reason: "", recovery_start_month: "", recovery_amount_per_month: 0, notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this advance? This will not reverse any recoveries already applied.")) return;
    try { await axios.delete(`${API}/payroll/advances/${id}`, { headers: getAuthHeader() }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const totalOutstanding = rows.filter(r => r.status !== "cleared").reduce((s, r) => s + Number(r.balance || 0), 0);

  return (
    <div className="space-y-4" data-testid="payroll-advances-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Advance Management</h1>
          <p className="text-sm text-slate-500 mt-1">Cash advances given to employees — recovered automatically from monthly payroll.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-slate-500">Outstanding</div>
            <div className="text-lg font-bold text-orange-600">{formatMoney(totalOutstanding)}</div>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="payroll-advance-add"><Plus className="w-4 h-4 mr-1" /> New Advance</Button>
        </div>
      </div>
      <Card><CardContent className="pt-4">
        {loading ? <div className="text-sm text-slate-500 text-center py-6">Loading…</div> :
          rows.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">No advances yet.</div> :
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Employee</TableHead><TableHead>Date</TableHead><TableHead>Reason</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Recovery / Month</TableHead>
              <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map(a => (
              <TableRow key={a.id} data-testid={`payroll-advance-${a.id}`}>
                <TableCell className="font-mono text-xs">{a.employee_id}</TableCell>
                <TableCell className="text-xs">{a.date}</TableCell>
                <TableCell>{a.reason || "—"}</TableCell>
                <TableCell className="text-right">{formatMoney(a.amount)}</TableCell>
                <TableCell className="text-right">{formatMoney(a.recovery_amount_per_month)}</TableCell>
                <TableCell className="text-right font-medium">{formatMoney(a.balance)}</TableCell>
                <TableCell><Badge variant={a.status === "cleared" ? "secondary" : "default"} className="text-xs">{a.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {isAdmin && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>}
      </CardContent></Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="payroll-advance-dialog">
          <DialogHeader><DialogTitle>New Advance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Employee</Label><EmpSelect value={form.employee_id} onChange={v => setForm({ ...form, employee_id: v })} testid="advance-emp" /></div>
            <Field label="Amount (₹)" type="number" value={form.amount} onChange={v => setForm({ ...form, amount: Number(v) || 0 })} testid="advance-amt" />
            <Field label="Reason" value={form.reason} onChange={v => setForm({ ...form, reason: v })} testid="advance-reason" />
            <Field label="Recovery Start Month (YYYY-MM, blank = immediate)" value={form.recovery_start_month} onChange={v => setForm({ ...form, recovery_start_month: v })} testid="advance-start" />
            <Field label="Recovery Amount / Month (₹)" type="number" value={form.recovery_amount_per_month} onChange={v => setForm({ ...form, recovery_amount_per_month: Number(v) || 0 })} testid="advance-monthly" />
            <Field label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="advance-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="payroll-advance-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------------- Loans tab ---------------------- */

const LoansTab = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", principal: 0, interest_rate: 0, emi: 0, tenure_months: 0, start_month: "", notes: "" });
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/payroll/loans`, { headers: getAuthHeader() }); setRows(r.data || []); }
    catch { toast.error("Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    if (!(form.principal > 0)) { toast.error("Principal must be > 0"); return; }
    if (!(form.emi > 0)) { toast.error("EMI must be > 0"); return; }
    try {
      await axios.post(`${API}/payroll/loans`, form, { headers: getAuthHeader() });
      toast.success("Loan recorded");
      setDialogOpen(false); load();
      setForm({ employee_id: "", principal: 0, interest_rate: 0, emi: 0, tenure_months: 0, start_month: "", notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this loan?")) return;
    try { await axios.delete(`${API}/payroll/loans/${id}`, { headers: getAuthHeader() }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const totalOutstanding = rows.filter(r => r.status !== "cleared").reduce((s, r) => s + Number(r.remaining_balance || 0), 0);

  return (
    <div className="space-y-4" data-testid="payroll-loans-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Loan Management</h1>
          <p className="text-sm text-slate-500 mt-1">Employee loans with EMI-based monthly recovery (optional flat-rate interest).</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><div className="text-xs uppercase text-slate-500">Total Outstanding</div><div className="text-lg font-bold text-orange-600">{formatMoney(totalOutstanding)}</div></div>
          <Button onClick={() => setDialogOpen(true)} data-testid="payroll-loan-add"><Plus className="w-4 h-4 mr-1" /> New Loan</Button>
        </div>
      </div>
      <Card><CardContent className="pt-4">
        {rows.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">No loans yet.</div> :
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Employee</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Principal</TableHead><TableHead className="text-right">Interest %</TableHead>
              <TableHead className="text-right">Total Payable</TableHead><TableHead className="text-right">EMI</TableHead>
              <TableHead className="text-right">Remaining</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map(l => (
              <TableRow key={l.id} data-testid={`payroll-loan-${l.id}`}>
                <TableCell className="font-mono text-xs">{l.employee_id}</TableCell>
                <TableCell className="text-xs">{l.date}</TableCell>
                <TableCell className="text-right">{formatMoney(l.principal)}</TableCell>
                <TableCell className="text-right">{l.interest_rate || 0}%</TableCell>
                <TableCell className="text-right">{formatMoney(l.total_payable)}</TableCell>
                <TableCell className="text-right">{formatMoney(l.emi)}</TableCell>
                <TableCell className="text-right font-medium">{formatMoney(l.remaining_balance)}</TableCell>
                <TableCell><Badge variant={l.status === "cleared" ? "secondary" : "default"} className="text-xs">{l.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {isAdmin && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(l.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>}
      </CardContent></Card>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="payroll-loan-dialog">
          <DialogHeader><DialogTitle>New Loan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Employee</Label><EmpSelect value={form.employee_id} onChange={v => setForm({ ...form, employee_id: v })} testid="loan-emp" /></div>
            <Field label="Principal (₹)" type="number" value={form.principal} onChange={v => setForm({ ...form, principal: Number(v) || 0 })} testid="loan-principal" />
            <Field label="Interest Rate % (flat, 0 = interest-free)" type="number" value={form.interest_rate} onChange={v => setForm({ ...form, interest_rate: Number(v) || 0 })} testid="loan-interest" />
            <Field label="EMI / Month (₹)" type="number" value={form.emi} onChange={v => setForm({ ...form, emi: Number(v) || 0 })} testid="loan-emi" />
            <Field label="Tenure (months)" type="number" value={form.tenure_months} onChange={v => setForm({ ...form, tenure_months: Number(v) || 0 })} testid="loan-tenure" />
            <Field label="Start Month (YYYY-MM)" value={form.start_month} onChange={v => setForm({ ...form, start_month: v })} testid="loan-start" />
            <Field label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="loan-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="payroll-loan-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------------- Adjustments tab ---------------------- */

const ADJ_TYPES = [
  { value: "bonus", label: "Bonus", sign: 1 },
  { value: "festival_bonus", label: "Festival Bonus", sign: 1 },
  { value: "performance_bonus", label: "Performance Bonus", sign: 1 },
  { value: "special_bonus", label: "Special Bonus", sign: 1 },
  { value: "reimbursement", label: "Reimbursement (Other)", sign: 1 },
  { value: "reimbursement_travel", label: "Reimbursement — Travel", sign: 1 },
  { value: "reimbursement_food", label: "Reimbursement — Food", sign: 1 },
  { value: "reimbursement_fuel", label: "Reimbursement — Fuel", sign: 1 },
  { value: "penalty", label: "Penalty", sign: -1 },
  { value: "damage", label: "Damage Recovery", sign: -1 },
  { value: "fine", label: "Fine", sign: -1 },
];

const AdjustmentsTab = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", type: "bonus", amount: 0, reason: "", date: "" });
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/payroll/adjustments`, { headers: getAuthHeader() }); setRows(r.data || []); }
    catch { toast.error("Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.employee_id || !(form.amount > 0)) { toast.error("Employee + positive amount required"); return; }
    try {
      await axios.post(`${API}/payroll/adjustments`, form, { headers: getAuthHeader() });
      toast.success("Adjustment recorded");
      setDialogOpen(false); load();
      setForm({ employee_id: "", type: "bonus", amount: 0, reason: "", date: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this adjustment?")) return;
    try { await axios.delete(`${API}/payroll/adjustments/${id}`, { headers: getAuthHeader() }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const signOf = (t) => (ADJ_TYPES.find(x => x.value === t)?.sign) || 1;

  return (
    <div className="space-y-4" data-testid="payroll-adjustments-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Salary Adjustments</h1>
          <p className="text-sm text-slate-500 mt-1">Bonuses, penalties, reimbursements — applied to the next unlocked payroll for that employee.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="payroll-adj-add"><Plus className="w-4 h-4 mr-1" /> New Adjustment</Button>
      </div>
      <Card><CardContent className="pt-4">
        {rows.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">No adjustments yet.</div> :
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Employee</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Applied?</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map(a => (
              <TableRow key={a.id} data-testid={`payroll-adj-${a.id}`}>
                <TableCell className="font-mono text-xs">{a.employee_id}</TableCell>
                <TableCell className="text-xs">{a.date}</TableCell>
                <TableCell><Badge variant={signOf(a.type) > 0 ? "default" : "destructive"} className="text-xs">{a.type}</Badge></TableCell>
                <TableCell>{a.reason || "—"}</TableCell>
                <TableCell className={`text-right font-medium ${signOf(a.type) > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {signOf(a.type) > 0 ? "+" : "−"}{formatMoney(a.amount)}
                </TableCell>
                <TableCell>{a.applied_batch_id ? <Badge className="text-xs">applied</Badge> : <Badge variant="outline" className="text-xs">pending</Badge>}</TableCell>
                <TableCell className="text-right">
                  {isAdmin && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>}
      </CardContent></Card>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="payroll-adj-dialog">
          <DialogHeader><DialogTitle>New Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Employee</Label><EmpSelect value={form.employee_id} onChange={v => setForm({ ...form, employee_id: v })} testid="adj-emp" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1" data-testid="adj-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ADJ_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label} {t.sign > 0 ? "(+)" : "(−)"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Amount (₹)" type="number" value={form.amount} onChange={v => setForm({ ...form, amount: Number(v) || 0 })} testid="adj-amt" />
            <Field label="Reason" value={form.reason} onChange={v => setForm({ ...form, reason: v })} testid="adj-reason" />
            <Field label="Date (blank = today)" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} testid="adj-date" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="payroll-adj-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------------- Payment Register tab ---------------------- */

const PaymentsTab = () => {
  const { batches } = useBatches();
  const [batchId, setBatchId] = useState("");
  const [register, setRegister] = useState(null);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payForm, setPayForm] = useState({ employee_id: "", amount: 0, mode: "upi", reference: "", notes: "" });

  useEffect(() => { if (!batchId && batches.length) setBatchId(batches[0].id); }, [batches, batchId]);
  const load = useCallback(async () => {
    if (!batchId) return;
    try { const r = await axios.get(`${API}/payroll/payments/register/${batchId}`, { headers: getAuthHeader() }); setRegister(r.data); }
    catch { toast.error("Load failed"); }
  }, [batchId]);
  useEffect(() => { load(); }, [load]);

  const openPay = (row) => {
    setPayForm({ employee_id: row.employee_id, amount: row.balance, mode: "upi", reference: "", notes: "" });
    setPayDialogOpen(true);
  };
  const savePay = async () => {
    if (!(payForm.amount > 0)) { toast.error("Amount required"); return; }
    try {
      await axios.post(`${API}/payroll/payments`, { ...payForm, batch_id: batchId }, { headers: getAuthHeader() });
      toast.success("Payment recorded");
      setPayDialogOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4" data-testid="payroll-payments-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment Register</h1>
          <p className="text-sm text-slate-500 mt-1">Record how each employee was paid — Cash / UPI / Bank / Cheque with reference numbers.</p>
        </div>
        <BatchSelector batchId={batchId} onChange={setBatchId} batches={batches} />
      </div>

      {register && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Total Payable" value={formatMoney(register.totals.payable)} icon={IndianRupee} tint="indigo" />
          <StatCard label="Total Paid" value={formatMoney(register.totals.paid)} icon={CheckCircle2} tint="emerald" />
          <StatCard label="Outstanding" value={formatMoney(register.totals.outstanding)} icon={AlertCircle} tint="orange" />
        </div>
      )}

      <Card><CardContent className="pt-4">
        {!register ? <div className="text-sm text-slate-500 text-center py-6">Loading…</div> :
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Emp ID</TableHead><TableHead>Name</TableHead><TableHead>Dept</TableHead>
              <TableHead className="text-right">Net Payable</TableHead><TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>{register.rows.map(r => (
              <TableRow key={r.employee_id} data-testid={`payroll-payreg-${r.employee_id}`}>
                <TableCell className="font-mono text-xs">{r.employee_id}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.department || "—"}</TableCell>
                <TableCell className="text-right">{formatMoney(r.net_payable)}</TableCell>
                <TableCell className="text-right text-emerald-700">{formatMoney(r.paid)}</TableCell>
                <TableCell className="text-right text-orange-600 font-medium">{formatMoney(r.balance)}</TableCell>
                <TableCell>
                  <Badge variant={r.status === "paid" ? "default" : r.status === "partial" ? "secondary" : "outline"} className="text-xs">{r.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => openPay(r)} disabled={r.balance <= 0} data-testid={`payroll-pay-btn-${r.employee_id}`}>
                    Record Payment
                  </Button>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>}
      </CardContent></Card>

      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-md" data-testid="payroll-pay-dialog">
          <DialogHeader><DialogTitle>Record Payment — {payForm.employee_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Amount (₹)" type="number" value={payForm.amount} onChange={v => setPayForm({ ...payForm, amount: Number(v) || 0 })} testid="pay-amt" />
            <div>
              <Label>Mode</Label>
              <Select value={payForm.mode} onValueChange={v => setPayForm({ ...payForm, mode: v })}>
                <SelectTrigger className="mt-1" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Reference / Transaction ID" value={payForm.reference} onChange={v => setPayForm({ ...payForm, reference: v })} testid="pay-ref" />
            <Field label="Notes" value={payForm.notes} onChange={v => setPayForm({ ...payForm, notes: v })} testid="pay-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialogOpen(false)}>Cancel</Button>
            <Button onClick={savePay} data-testid="payroll-pay-save">Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------------------- Employee Ledger tab ---------------------- */

const LEDGER_TYPE_LABELS = {
  salary:            { label: "Salary Credit", color: "text-emerald-700", icon: ArrowUpRight },
  advance_taken:     { label: "Advance Taken", color: "text-orange-700", icon: ArrowDownRight },
  advance_recovery:  { label: "Advance Recovery", color: "text-emerald-700", icon: ArrowUpRight },
  loan_taken:        { label: "Loan Disbursed", color: "text-orange-700", icon: ArrowDownRight },
  loan_emi:          { label: "Loan EMI", color: "text-emerald-700", icon: ArrowUpRight },
  payment:           { label: "Payment Made", color: "text-rose-700", icon: ArrowDownRight },
};

const LedgerTab = () => {
  const [empId, setEmpId] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const emps = useEmployees();

  useEffect(() => { if (!empId && emps.length) setEmpId(emps[0].employee_id); }, [emps, empId]);
  useEffect(() => {
    if (!empId) return;
    setLoading(true);
    axios.get(`${API}/payroll/employees/${empId}/ledger`, { headers: getAuthHeader() })
      .then(r => setLedger(r.data))
      .catch(() => toast.error("Failed to load ledger"))
      .finally(() => setLoading(false));
  }, [empId]);

  const labelFor = (t) => LEDGER_TYPE_LABELS[t] || { label: t, color: "text-slate-600", icon: ArrowUpRight };

  return (
    <div className="space-y-4" data-testid="payroll-ledger-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employee Ledger</h1>
          <p className="text-sm text-slate-500 mt-1">Permanent per-employee timeline — salary credits, advances, loans, adjustments, payments — with running balance.</p>
        </div>
        <div className="w-[280px]"><EmpSelect value={empId} onChange={setEmpId} testid="ledger-emp-select" /></div>
      </div>

      {ledger && ledger.employee && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg">{ledger.employee.name || ledger.employee.employee_id}</CardTitle>
                <div className="text-xs text-slate-500">{ledger.employee.department} · {ledger.employee.designation}</div>
              </div>
              <div className="flex gap-3 text-right">
                <div><div className="text-[10px] uppercase text-slate-500">Credits</div><div className="text-sm font-bold text-emerald-700">{formatMoney(ledger.totals.total_credits)}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500">Debits</div><div className="text-sm font-bold text-rose-700">{formatMoney(ledger.totals.total_debits)}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500">Closing</div>
                  <div className={`text-sm font-bold ${ledger.totals.closing_balance >= 0 ? "text-emerald-800" : "text-rose-800"}`}>
                    {formatMoney(ledger.totals.closing_balance)}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      <Card><CardContent className="pt-4">
        {loading ? <div className="text-sm text-slate-500 text-center py-6">Loading…</div> :
          !ledger || ledger.events.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">No ledger activity yet.</div> :
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead>
              <TableHead className="text-right">Credit</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow></TableHeader>
            <TableBody>{ledger.events.map((e, i) => {
              const meta = labelFor(e.type);
              const Icon = meta.icon;
              return (
                <TableRow key={i} data-testid={`payroll-ledger-row-${i}`}>
                  <TableCell className="text-xs whitespace-nowrap">{e.date}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{e.label}</TableCell>
                  <TableCell className="text-right text-emerald-700">{e.credit > 0 ? formatMoney(e.credit) : "—"}</TableCell>
                  <TableCell className="text-right text-rose-700">{e.debit > 0 ? formatMoney(e.debit) : "—"}</TableCell>
                  <TableCell className={`text-right font-medium ${e.balance >= 0 ? "text-slate-800" : "text-rose-700"}`}>{formatMoney(e.balance)}</TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table></div>}
      </CardContent></Card>
    </div>
  );
};

/* ---------------------- Settings (Company block + Logo upload) ---------------------- */

const EMPTY_COMPANY = {
  name: "", address_line_1: "", address_line_2: "", address_line_3: "",
  city: "", state: "", pincode: "", gstin: "",
};

const SettingsTab = ({ user }) => {
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const logoRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const fetchLogo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payroll/company/logo`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch { setLogoUrl(null); }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/payroll/company`, { headers: getAuthHeader() });
      setCompany({ ...EMPTY_COMPANY, ...(res.data || {}) });
      setHasLogo(!!res.data?.has_logo);
      if (res.data?.has_logo) fetchLogo();
    } catch { toast.error("Failed to load company"); }
    finally { setLoading(false); }
  }, [fetchLogo]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/payroll/company`, company, { headers: getAuthHeader() });
      toast.success("Company details saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/payroll/company/logo`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      await fetchLogo();
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    if (logoRef.current) logoRef.current.value = "";
  };
  const removeLogo = async () => {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await axios.delete(`${API}/payroll/company/logo`, { headers: getAuthHeader() });
      setHasLogo(false);
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      toast.success("Logo removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="payroll-settings-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Company & Slip Settings</h1>
        <p className="text-sm text-slate-500 mt-1">This company block appears on every salary slip PDF. Salary calculation rules live under the &ldquo;Salary Rules&rdquo; tab.</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Company Logo</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
              {hasLogo && logoUrl ? (
                <img src={logoUrl}
                     alt="Company Logo"
                     className="w-full h-full object-contain"
                     data-testid="payroll-company-logo" />
              ) : (
                <Camera className="w-8 h-8 text-slate-300" />
              )}
            </div>
            {isAdmin && (
              <div className="space-y-2">
                <input ref={logoRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <Button variant="outline" onClick={() => logoRef.current?.click()} data-testid="payroll-logo-upload">
                  <UploadCloud className="w-4 h-4 mr-1" /> Upload Logo (PNG/JPG, ≤3MB)
                </Button>
                {hasLogo && (
                  <Button variant="ghost" className="text-rose-600" onClick={removeLogo} data-testid="payroll-logo-remove">
                    <Trash2 className="w-4 h-4 mr-1" /> Remove Logo
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Company Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company Name" value={company.name} onChange={(v) => setCompany({ ...company, name: v })} disabled={!isAdmin} testid="company-name" />
          <Field label="GSTIN" value={company.gstin} onChange={(v) => setCompany({ ...company, gstin: v })} disabled={!isAdmin} testid="company-gstin" />
          <Field label="Address Line 1" value={company.address_line_1} onChange={(v) => setCompany({ ...company, address_line_1: v })} disabled={!isAdmin} testid="company-a1" />
          <Field label="Address Line 2" value={company.address_line_2} onChange={(v) => setCompany({ ...company, address_line_2: v })} disabled={!isAdmin} testid="company-a2" />
          <Field label="Address Line 3" value={company.address_line_3} onChange={(v) => setCompany({ ...company, address_line_3: v })} disabled={!isAdmin} testid="company-a3" />
          <Field label="City" value={company.city} onChange={(v) => setCompany({ ...company, city: v })} disabled={!isAdmin} testid="company-city" />
          <Field label="State" value={company.state} onChange={(v) => setCompany({ ...company, state: v })} disabled={!isAdmin} testid="company-state" />
          <Field label="Pincode" value={company.pincode} onChange={(v) => setCompany({ ...company, pincode: v })} disabled={!isAdmin} testid="company-pincode" />
        </CardContent>
      </Card>
      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="company-save">{saving ? "Saving…" : "Save Company"}</Button>
        </div>
      )}
    </div>
  );
};

/* ---------------------- Reports tab (Phase 4) ---------------------- */

const REPORT_SHEETS = [
  { name: "Summary",            desc: "Batch totals — gross, net, employees" },
  { name: "Salary Register",    desc: "All employees × all batches breakdown" },
  { name: "Attendance",         desc: "Per-employee attendance stats" },
  { name: "Department Salary",  desc: "Grouped totals by department" },
  { name: "Late Report",        desc: "Top late-comers by count" },
  { name: "OT Report",          desc: "Total OT hours per employee" },
  { name: "Attendance Bonus",   desc: "Zero-absence bonus earners" },
  { name: "Absent Report",      desc: "Highest absent/half-day employees" },
  { name: "Advances",           desc: "All advances with balances" },
  { name: "Loans",              desc: "All loans with EMI + remaining" },
  { name: "PF Report",          desc: "12% PF contribution + employer share" },
  { name: "ESIC Report",        desc: "0.75% + 3.25% ESIC contribution" },
  { name: "Payments",           desc: "All payments made in period" },
];

const ReportsTab = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setDateFrom(first); setDateTo(last);
  }, []);

  const loadSummary = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    try {
      const r = await axios.get(`${API}/payroll/reports/period-summary`,
        { headers: getAuthHeader(), params: { date_from: dateFrom, date_to: dateTo } });
      setSummary(r.data);
    } catch { toast.error("Failed to load"); }
  }, [dateFrom, dateTo]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/payroll/reports/all.xlsx`,
        { headers: getAuthHeader(), params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `payroll_reports_${dateFrom}_${dateTo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Reports downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4" data-testid="payroll-reports-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Comprehensive payroll reports as a single multi-sheet Excel workbook (13 sheets).</p>
      </div>
      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="From Date" type="date" value={dateFrom} onChange={setDateFrom} testid="reports-from" />
          <Field label="To Date" type="date" value={dateTo} onChange={setDateTo} testid="reports-to" />
          <Button onClick={loadSummary} variant="outline" data-testid="reports-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button onClick={download} disabled={downloading || !dateFrom || !dateTo} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reports-download">
            <Download className="w-4 h-4 mr-1" /> {downloading ? "Downloading…" : "Download All Reports (Excel)"}
          </Button>
        </div>
      </CardContent></Card>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Batches in Range" value={summary.batch_count} icon={CalendarDays} tint="indigo" />
          <StatCard label="Locked" value={summary.locked_batches} icon={Lock} tint="emerald" sub="Finalized payrolls" />
          <StatCard label="Total Gross" value={formatMoney(summary.total_gross)} icon={TrendingUp} tint="violet" />
          <StatCard label="Total Net Paid" value={formatMoney(summary.total_net)} icon={IndianRupee} tint="emerald" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Report Sheets in the Workbook</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-1.5">
              {REPORT_SHEETS.map((s, i) => (
                <div key={s.name} className="flex items-start gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="w-6 h-6 rounded bg-indigo-50 text-indigo-700 flex items-center justify-center text-[11px] font-semibold shrink-0">{i + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        {summary && (
          <Card>
            <CardHeader><CardTitle className="text-base">Batches in Range</CardTitle></CardHeader>
            <CardContent>
              {summary.batches.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-6">No batches in this range.</div>
              ) : (
                <div className="overflow-x-auto"><Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Filename</TableHead><TableHead>Period</TableHead>
                    <TableHead className="text-right">Emp</TableHead>
                    <TableHead className="text-right">Net</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{summary.batches.map(b => (
                    <TableRow key={b.id} data-testid={`reports-batch-${b.id}`}>
                      <TableCell className="text-xs">{b.filename}</TableCell>
                      <TableCell className="text-xs">{b.period_from} → {b.period_to}</TableCell>
                      <TableCell className="text-right">{b.total_employees}</TableCell>
                      <TableCell className="text-right">{formatMoney(b.total_net)}</TableCell>
                      <TableCell><Badge variant={b.status === "locked" ? "default" : "outline"} className="text-xs">{b.status}</Badge></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table></div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};



/* ---------------------- Placeholder tab ---------------------- */

/* ---------------------- Batch selector (shared across P2 tabs) ---------------------- */

const useBatches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/payroll/attendance`, { headers: getAuthHeader() });
      setBatches(res.data || []);
    } catch { toast.error("Failed to load batches"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { batches, loading, reload: load };
};

const BatchSelector = ({ batchId, onChange, batches }) => (
  <Select value={batchId || ""} onValueChange={onChange}>
    <SelectTrigger className="w-[360px]" data-testid="payroll-batch-select">
      <SelectValue placeholder="Select an attendance batch…" />
    </SelectTrigger>
    <SelectContent>
      {batches.map((b) => (
        <SelectItem key={b.id} value={b.id}>
          {b.filename} · {b.period_from} → {b.period_to} · {b.total_employees} emp
          {b.status === "locked" ? " · 🔒" : ""}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/* ---------------------- Attendance Calculation tab ---------------------- */

const CalculationTab = () => {
  const { batches } = useBatches();
  const [batchId, setBatchId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!batchId && batches.length) setBatchId(batches[0].id);
  }, [batches, batchId]);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    axios.get(`${API}/payroll/attendance/${batchId}/summary`, { headers: getAuthHeader() })
      .then(r => setSummary(r.data))
      .catch(() => toast.error("Failed to load summary"))
      .finally(() => setLoading(false));
  }, [batchId]);

  return (
    <div className="space-y-4" data-testid="payroll-calculation-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance Calculation</h1>
          <p className="text-sm text-slate-500 mt-1">Per-employee monthly attendance computed from punches — half-day, late, OT, attendance %.</p>
        </div>
        <BatchSelector batchId={batchId} onChange={setBatchId} batches={batches} />
      </div>
      <Card>
        <CardContent className="pt-4">
          {loading || !summary ? (
            <div className="text-sm text-slate-500 text-center py-6">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Emp ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Working</TableHead>
                    <TableHead className="text-right">Present</TableHead>
                    <TableHead className="text-right">Absent</TableHead>
                    <TableHead className="text-right">Half</TableHead>
                    <TableHead className="text-right">W-Off</TableHead>
                    <TableHead className="text-right">Holiday</TableHead>
                    <TableHead className="text-right">Paid Leave</TableHead>
                    <TableHead className="text-right">Late</TableHead>
                    <TableHead className="text-right">OT Hrs</TableHead>
                    <TableHead className="text-right">Payable</TableHead>
                    <TableHead className="text-right">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.rows.map((r) => (
                    <TableRow key={r.employee_id} data-testid={`payroll-calc-row-${r.employee_id}`}>
                      <TableCell className="font-mono text-xs">{r.employee_id}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right">{r.working_days}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-medium">{r.present}</TableCell>
                      <TableCell className="text-right text-rose-700">{r.absent}</TableCell>
                      <TableCell className="text-right text-amber-700">{r.half_day}</TableCell>
                      <TableCell className="text-right text-slate-500">{r.week_off}</TableCell>
                      <TableCell className="text-right text-slate-500">{r.holiday}</TableCell>
                      <TableCell className="text-right text-sky-600">{r.paid_leave}</TableCell>
                      <TableCell className="text-right text-orange-600">{r.late_days}</TableCell>
                      <TableCell className="text-right">{r.ot_hours}</TableCell>
                      <TableCell className="text-right font-medium">{r.payable_days}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={r.attendance_pct >= 90 ? "default" : r.attendance_pct >= 70 ? "secondary" : "destructive"} className="text-[10px]">
                          {r.attendance_pct}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------------- Salary Rules tab ---------------------- */

const RulesTab = ({ user }) => {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    axios.get(`${API}/payroll/settings`, { headers: getAuthHeader() })
      .then(r => setRules(r.data))
      .catch(() => toast.error("Failed to load rules"));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/payroll/settings`, rules, { headers: getAuthHeader() });
      toast.success("Salary rules saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!rules) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="payroll-rules-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Salary Rules</h1>
        <p className="text-sm text-slate-500 mt-1">Company-wide rules driving every salary calculation. Admin-editable.</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Attendance & Working Hours</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Standard Working Hours / Day" type="number" value={rules.standard_hours}
                 onChange={(v) => setRules({ ...rules, standard_hours: Number(v) || 8 })} disabled={!isAdmin} testid="rules-std-hours" />
          <Field label="Grace Threshold (hours for Full Day)" type="number" value={Number(rules.grace_hours || 0).toFixed(2)}
                 onChange={(v) => setRules({ ...rules, grace_hours: Number(v) || 0 })} disabled={!isAdmin} testid="rules-grace" />
          <Field label="Half-Day Minimum Hours" type="number" value={rules.half_day_min_hours ?? 4}
                 onChange={(v) => setRules({ ...rules, half_day_min_hours: Number(v) || 0 })} disabled={!isAdmin} testid="rules-half" />
          <Field label="Lunch Break (minutes)" type="number" value={rules.lunch_break_minutes ?? 30}
                 onChange={(v) => setRules({ ...rules, lunch_break_minutes: Number(v) || 0 })} disabled={!isAdmin} testid="rules-lunch" />
          <div>
            <Label className="text-xs text-slate-600">Weekly Off Day</Label>
            <Select value={rules.weekly_off_day} onValueChange={(v) => setRules({ ...rules, weekly_off_day: v })} disabled={!isAdmin}>
              <SelectTrigger className="mt-1" data-testid="rules-weekly-off"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Attendance Bonus (extra day on zero-absence)</Label>
            <Select value={String(rules.attendance_bonus_enabled)} onValueChange={(v) => setRules({ ...rules, attendance_bonus_enabled: v === "true" })} disabled={!isAdmin}>
              <SelectTrigger className="mt-1" data-testid="rules-bonus"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Overtime & Deductions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600">
              Pay for Overtime Hours
              <span className="text-slate-400 font-normal ml-1">(extra hours beyond standard)</span>
            </Label>
            <Select value={String(rules.pay_overtime ?? false)}
                    onValueChange={(v) => setRules({ ...rules, pay_overtime: v === "true" })}
                    disabled={!isAdmin}>
              <SelectTrigger className="mt-1" data-testid="rules-pay-overtime"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="false">No — extra hours don&apos;t add pay (tracked only)</SelectItem>
                <SelectItem value="true">Yes — pay OT hours × hourly rate × multiplier</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Less-than-standard hours are still deducted pro-rata via half-day / absent rules.
            </p>
          </div>
          <Field label="OT Multiplier (only used when OT pay is enabled)"
                 type="number" value={rules.ot_multiplier}
                 onChange={(v) => setRules({ ...rules, ot_multiplier: Number(v) || 1 })}
                 disabled={!isAdmin || !(rules.pay_overtime ?? false)}
                 testid="rules-ot" />
          <Field label="Salary Round Off (0 = no rounding)" type="number" value={rules.round_off}
                 onChange={(v) => setRules({ ...rules, round_off: Number(v) || 0 })} disabled={!isAdmin} testid="rules-round" />
          <div>
            <Label className="text-xs text-slate-600">Late Deduction Rule</Label>
            <Select value={rules.late_deduction || "ignore"} onValueChange={(v) => setRules({ ...rules, late_deduction: v })} disabled={!isAdmin}>
              <SelectTrigger className="mt-1" data-testid="rules-late"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ignore">Ignore (track only, no deduction)</SelectItem>
                <SelectItem value="per_3_days">Every 3 late days = 1 day cut</SelectItem>
                <SelectItem value="per_day_amount">Fixed ₹ per late day</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {rules.late_deduction === "per_day_amount" && (
            <Field label="Fine per Late Day (₹)" type="number" value={rules.late_fine_per_day ?? 0}
                   onChange={(v) => setRules({ ...rules, late_fine_per_day: Number(v) || 0 })} disabled={!isAdmin} testid="rules-late-amt" />
          )}
        </CardContent>
      </Card>
      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="rules-save">{saving ? "Saving…" : "Save Rules"}</Button>
        </div>
      )}
    </div>
  );
};

/* ---------------------- Payroll Processing tab ---------------------- */

const ProcessingTab = ({ user }) => {
  const { batches, reload: reloadBatches } = useBatches();
  const [batchId, setBatchId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => { if (!batchId && batches.length) setBatchId(batches[0].id); }, [batches, batchId]);

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payroll/attendance/${batchId}/preview`, { headers: getAuthHeader() });
      setPreview(res.data);
    } catch { toast.error("Preview failed"); }
    finally { setLoading(false); }
  }, [batchId]);
  useEffect(() => { load(); }, [load]);

  const lock = async () => {
    if (!window.confirm(`Lock payroll for this batch?\n\nTotal Net: ${formatMoney(preview?.total_net)}\n${preview?.employee_count} employees\n\nAfter locking, salary values are frozen. Only admins can reopen.`)) return;
    setAction(true);
    try {
      await axios.post(`${API}/payroll/attendance/${batchId}/lock`, {}, { headers: getAuthHeader() });
      toast.success("Payroll locked");
      load();
      reloadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lock failed"); }
    finally { setAction(false); }
  };
  const unlock = async () => {
    if (!window.confirm("Reopen (unlock) this payroll batch? Snapshot will be discarded.")) return;
    setAction(true);
    try {
      await axios.post(`${API}/payroll/attendance/${batchId}/unlock`, {}, { headers: getAuthHeader() });
      toast.success("Payroll reopened");
      load();
      reloadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); }
    finally { setAction(false); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const res = await axios.get(`${API}${path}`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const locked = preview?.status === "locked";

  return (
    <div className="space-y-4" data-testid="payroll-processing-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll Processing</h1>
          <p className="text-sm text-slate-500 mt-1">One-click: preview → lock → download final sheet + slips.</p>
        </div>
        <BatchSelector batchId={batchId} onChange={setBatchId} batches={batches} />
      </div>

      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard label="Employees" value={preview.employee_count} icon={Users} tint="indigo" />
          <StatCard label="Total Gross" value={formatMoney(preview.total_gross)} icon={TrendingUp} tint="violet" />
          <StatCard label="Total Net Payable" value={formatMoney(preview.total_net)} icon={IndianRupee} tint="emerald" />
          <StatCard label="Status" value={locked ? "LOCKED" : "DRAFT"} icon={locked ? Lock : Unlock} tint={locked ? "emerald" : "amber"} sub={locked ? `by ${preview.locked_by} on ${new Date(preview.locked_at).toLocaleString()}` : "Preview only — not yet approved"} />
        </div>
      )}

      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {isAdmin && !locked && (
              <Button onClick={lock} disabled={action || !preview} className="bg-emerald-600 hover:bg-emerald-700" data-testid="payroll-lock-btn">
                <Lock className="w-4 h-4 mr-1" /> {action ? "Locking…" : "Lock Payroll"}
              </Button>
            )}
            {isAdmin && locked && (
              <Button variant="outline" onClick={unlock} disabled={action} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="payroll-unlock-btn">
                <Unlock className="w-4 h-4 mr-1" /> {action ? "Reopening…" : "Reopen"}
              </Button>
            )}
            <Button variant="outline" onClick={() => downloadFile(`/payroll/attendance/${batchId}/salary-sheet.xlsx`, `salary_sheet_${preview?.period_from || 'batch'}.xlsx`)} disabled={!preview} data-testid="payroll-download-sheet">
              <Download className="w-4 h-4 mr-1" /> Salary Sheet (Excel)
            </Button>
            <Button variant="outline" onClick={() => downloadFile(`/payroll/attendance/${batchId}/slips.zip`, `salary_slips_${preview?.period_from || 'batch'}.zip`)} disabled={!preview} data-testid="payroll-download-slips-zip">
              <Archive className="w-4 h-4 mr-1" /> All Slips (ZIP)
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 text-center py-6">Loading preview…</div>
          ) : preview && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Emp ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                    <TableHead className="text-right">Present</TableHead>
                    <TableHead className="text-right">Payable</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">OT</TableHead>
                    <TableHead className="text-right">Bonus</TableHead>
                    <TableHead className="text-right">Deduct</TableHead>
                    <TableHead className="text-right font-semibold">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map(r => (
                    <TableRow key={r.employee_id} data-testid={`payroll-process-row-${r.employee_id}`}>
                      <TableCell className="font-mono text-xs">{r.employee_id}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right">{formatMoney(r.monthly_salary)}</TableCell>
                      <TableCell className="text-right text-emerald-700">{r.summary.present}</TableCell>
                      <TableCell className="text-right">{r.summary.payable_days}</TableCell>
                      <TableCell className="text-right">{formatMoney(r.base_pay)}</TableCell>
                      <TableCell className="text-right">{formatMoney(r.ot_pay)}</TableCell>
                      <TableCell className="text-right text-emerald-700">{formatMoney(r.bonus)}</TableCell>
                      <TableCell className="text-right text-rose-700">{formatMoney(r.deductions)}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-800">{formatMoney(r.net)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------------- Salary Preview tab (single employee) ---------------------- */

const PreviewTab = () => {
  const { batches } = useBatches();
  const [batchId, setBatchId] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState("");

  useEffect(() => { if (!batchId && batches.length) setBatchId(batches[0].id); }, [batches, batchId]);
  useEffect(() => {
    if (!batchId) return;
    axios.get(`${API}/payroll/attendance/${batchId}/preview`, { headers: getAuthHeader() })
      .then(r => { setPreview(r.data); setSelected(r.data.rows?.[0]?.employee_id || ""); })
      .catch(() => toast.error("Failed to load preview"));
  }, [batchId]);

  const row = useMemo(() => preview?.rows.find(r => r.employee_id === selected), [preview, selected]);

  return (
    <div className="space-y-4" data-testid="payroll-preview-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Salary Preview</h1>
          <p className="text-sm text-slate-500 mt-1">Detailed per-employee breakdown before payroll is locked.</p>
        </div>
        <div className="flex items-center gap-2">
          <BatchSelector batchId={batchId} onChange={setBatchId} batches={batches} />
        </div>
      </div>

      {preview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Employees ({preview.rows.length})</CardTitle></CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto pt-0">
                {preview.rows.map(r => (
                  <button key={r.employee_id}
                          onClick={() => setSelected(r.employee_id)}
                          data-testid={`payroll-preview-emp-${r.employee_id}`}
                          className={`w-full text-left px-3 py-2 rounded flex items-center justify-between text-sm
                            ${selected === r.employee_id ? "bg-indigo-50 text-indigo-700 font-medium" : "hover:bg-slate-50"}`}>
                    <span>
                      <span className="font-mono text-xs text-slate-400 mr-2">{r.employee_id}</span>
                      {r.name || "—"}
                    </span>
                    <span className="text-xs">{formatMoney(r.net)}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
          <div className="md:col-span-2">
            {row ? (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{row.name || row.employee_id}</CardTitle>
                      <div className="text-xs text-slate-500 mt-1">{row.department} · {row.designation}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/payroll/attendance/${batchId}/slip/${row.employee_id}.pdf`,
                          { headers: getAuthHeader(), responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        window.open(url, "_blank");
                      } catch { toast.error("Slip download failed"); }
                    }} data-testid="payroll-preview-slip">
                      <FileText className="w-4 h-4 mr-1" /> View Slip
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <MiniStat label="Monthly" val={formatMoney(row.monthly_salary)} />
                    <MiniStat label="Per Day" val={formatMoney(row.per_day)} />
                    <MiniStat label="Attendance %" val={`${row.summary.attendance_pct}%`} />
                    <MiniStat label="Payable Days" val={row.summary.payable_days} />
                  </div>
                  <div className="border-t pt-3">
                    <div className="text-xs font-semibold text-slate-600 mb-2">EARNINGS</div>
                    <BreakRow label="Basic Pay" value={row.base_pay} />
                    <BreakRow label="Overtime" value={row.ot_pay} sub={`${row.summary.ot_hours}h @ ₹${row.hourly_salary}/hr`} />
                    <BreakRow label="Attendance Bonus" value={row.bonus} sub={row.bonus > 0 ? "Zero absence — +1 day" : "Not eligible"} />
                    <BreakRow label="Extra Bonuses" value={row.extra_bonuses || 0} sub={row.extra_bonuses > 0 ? "From adjustments" : ""} />
                    <BreakRow label="Reimbursements" value={row.reimbursements || 0} sub={row.reimbursements > 0 ? "Travel/Food/Fuel" : ""} />
                    <div className="border-t border-slate-200 mt-2 pt-2">
                      <BreakRow label="Total Earnings" value={row.gross} bold />
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <div className="text-xs font-semibold text-slate-600 mb-2">DEDUCTIONS</div>
                    <BreakRow label={`Late Deduction (${row.summary.late_days} late days)`} value={row.late_deduction} />
                    <BreakRow label="Advance Recovery" value={row.advance_recovery || 0} sub={row.advance_recovery > 0 ? "Applied this month" : "None"} />
                    <BreakRow label="Loan EMI" value={row.loan_emi || 0} sub={row.loan_emi > 0 ? "Applied this month" : "None"} />
                    <BreakRow label="Penalties" value={row.penalties || 0} />
                    <div className="border-t border-slate-200 mt-2 pt-2">
                      <BreakRow label="Total Deductions" value={row.deductions} bold />
                    </div>
                  </div>
                  <div className="border-t pt-3 bg-emerald-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                    <BreakRow label="NET SALARY PAYABLE" value={row.net} bold color="text-emerald-800" size="text-lg" />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="text-sm text-slate-500 text-center py-6">Select an employee to see breakdown.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ label, val }) => (
  <div className="bg-slate-50 rounded px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-800">{val}</div>
  </div>
);

const BreakRow = ({ label, value, sub, bold, color, size }) => (
  <div className="flex justify-between items-center py-1">
    <div>
      <div className={`${bold ? "font-semibold" : ""} ${size || "text-sm"} ${color || "text-slate-800"}`}>{label}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
    <div className={`${bold ? "font-semibold" : ""} ${size || "text-sm"} ${color || "text-slate-800"}`}>{formatMoney(value)}</div>
  </div>
);

/* ---------------------- Salary Slip tab ---------------------- */

const SlipTab = () => {
  const { batches } = useBatches();
  const [batchId, setBatchId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => { if (!batchId && batches.length) setBatchId(batches[0].id); }, [batches, batchId]);
  useEffect(() => {
    if (!batchId) return;
    axios.get(`${API}/payroll/attendance/${batchId}/summary`, { headers: getAuthHeader() })
      .then(r => { setEmployees(r.data.rows || []); if (r.data.rows?.[0]) setSelected(r.data.rows[0].employee_id); })
      .catch(() => setEmployees([]));
  }, [batchId]);

  useEffect(() => {
    if (!batchId || !selected) { setPdfUrl(null); return; }
    let objUrl;
    axios.get(`${API}/payroll/attendance/${batchId}/slip/${selected}.pdf`,
      { headers: getAuthHeader(), responseType: "blob" })
      .then(r => { objUrl = URL.createObjectURL(r.data); setPdfUrl(objUrl); })
      .catch(() => { setPdfUrl(null); toast.error("Failed to load slip"); });
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [batchId, selected]);

  const download = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a"); a.href = pdfUrl;
    a.download = `slip_${selected}_${batchId.slice(0,6)}.pdf`; a.click();
  };
  const printSlip = () => { if (pdfUrl) window.open(pdfUrl, "_blank"); };

  return (
    <div className="space-y-4" data-testid="payroll-slip-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Salary Slip</h1>
          <p className="text-sm text-slate-500 mt-1">Generate & download per-employee salary slip PDF.</p>
        </div>
        <div className="flex items-center gap-2">
          <BatchSelector batchId={batchId} onChange={setBatchId} batches={batches} />
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-[220px]" data-testid="payroll-slip-emp-select">
              <SelectValue placeholder="Select employee…" />
            </SelectTrigger>
            <SelectContent>
              {employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} — {e.name || "—"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={download} disabled={!pdfUrl} data-testid="payroll-slip-download">
              <Download className="w-4 h-4 mr-1" /> Download
            </Button>
            <Button size="sm" variant="outline" onClick={printSlip} disabled={!pdfUrl} data-testid="payroll-slip-print">
              <FileText className="w-4 h-4 mr-1" /> Open in New Tab
            </Button>
          </div>
          {pdfUrl ? (
            <iframe src={pdfUrl} className="w-full h-[700px] border rounded" title="Salary Slip Preview" data-testid="payroll-slip-iframe" />
          ) : (
            <div className="text-sm text-slate-500 text-center py-10">Select an employee to preview the slip.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------------------- PlaceholderTab (kept for Phase 3/4 modules) ---------------------- */

const PlaceholderTab = ({ title, phase, description }) => (
  <div className="max-w-2xl mx-auto text-center pt-16" data-testid="payroll-placeholder">
    <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
      <Settings2 className="w-8 h-8 text-slate-400" />
    </div>
    <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
    <div className="mt-1 text-sm text-slate-500">{description}</div>
    <Badge variant="outline" className="mt-4 text-slate-500">{phase}</Badge>
  </div>
);

export default PayrollPanel;
